var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as Rx from 'rxjs';
export function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
}
/**
 * Deeply merges two objects to create a new object, where the second object
 * does not need to have all properties of the first.
 */
export function deepSpread(original, delta) {
    if (delta == null) {
        return original;
    }
    if (!isObject(delta)) {
        return delta;
    }
    return __assign({}, original, mapValues(delta, function (value, key) {
        return deepSpread((original || {})[key], value);
    }));
}
export function sameElements(as, bs) {
    var setAs = new Set(as);
    var setBs = new Set(bs);
    return as.every(function (a) { return setBs.has(a); }) && bs.every(function (b) { return setAs.has(b); });
}
// All the (distinct) elements in `as` that isn't in `bs`.
export function setDiff(as, bs) {
    var setAs = new Set(as);
    var setBs = new Set(bs);
    return Array.from(setAs).filter(function (a) { return !setBs.has(a); });
}
export function assertNever(x, error) {
    if (error === void 0) { error = 'Unexpected object'; }
    throw new Error(error + ": " + JSON.stringify(x));
}
export var OrderByDirection;
(function (OrderByDirection) {
    OrderByDirection[OrderByDirection["ASC"] = 0] = "ASC";
    OrderByDirection[OrderByDirection["DESC"] = 1] = "DESC";
})(OrderByDirection || (OrderByDirection = {}));
/**
 * Returns a compare function that can be used by Array.prototype.sort()
 */
export function compareMultipleFields(_a) {
    var order = _a[0], orders = _a.slice(1);
    if (order === undefined) {
        return function () { return 0; };
    }
    var field = order.field, _b = order.direction, direction = _b === void 0 ? OrderByDirection.ASC : _b;
    return function (a, b) {
        if (a[field] < b[field]) {
            return direction === OrderByDirection.DESC ? 1 : -1;
        }
        if (a[field] > b[field]) {
            return direction === OrderByDirection.DESC ? -1 : 1;
        }
        return compareMultipleFields(orders)(a, b);
    };
}
/**
 * Returns an array with no duplicate elements, where uniqueness is defined
 * by the comparator function passed in. Preserves order of the original array.
 * If the comparator is not given, it defaults to '===' equality.
 *
 * Example: Get all unique names from users
 *
 * const names = uniqWith(users, (u1, u2) => u1.name === u2.name).map(u => u.name)
 */
export function uniqWith(xs, compareFunction) {
    if (compareFunction === void 0) { compareFunction = function (a, b) { return a === b; }; }
    return xs.reduce(function (acc, x) {
        return acc.find(function (y) { return compareFunction(x, y); }) ? acc : acc.concat([x]);
    }, []);
}
export function uniq(xs) {
    return uniqWith(xs, function (x, y) { return x === y; });
}
export function takeWhile(array, predicate) {
    var index = array.findIndex(function (v, i, c) { return !predicate(v, i, c); });
    return index === -1 ? array : array.slice(0, index);
}
export function flatMap(array, callbackfn) {
    return flatten(array.map(callbackfn));
}
export function chunk(array, size) {
    if (size === void 0) { size = 1; }
    var result = [];
    for (var index = 0; index < array.length; index += size) {
        result.push(array.slice(index, index + size));
    }
    return result;
}
export function isDefined(x) {
    return x !== undefined && x !== null;
}
export function objectFilter(object, predicate) {
    return Object.entries(object)
        .filter(function (_a) {
        var key = _a[0], value = _a[1];
        return predicate(key, value);
    })
        .reduce(function (obj, _a) {
        var key = _a[0], value = _a[1];
        obj[key] = value;
        return obj;
    }, {});
}
export function objectFilterByKeys(object, keys) {
    return objectFilter(object, function (key) { return keys.includes(key); });
}
export function objectKeysFilter(object, predicate) {
    return Object.entries(object)
        .filter(function (_a) {
        var key = _a[0], value = _a[1];
        return value && predicate(value, key);
    })
        .map(function (_a) {
        var key = _a[0], value = _a[1];
        return key;
    });
}
export function arrayIsShallowEqual(a, b) {
    return Boolean(a &&
        b &&
        a.length === b.length &&
        a.every(function (entity, index) { return entity === b[index]; }));
}
export function toObservable(x$) {
    return Rx.isObservable(x$) ? x$ : Rx.of(x$);
}
/**
 * Splits an array into two groups, the first containing all elements which
 * were truthy when evaluated for the given projection function, and the second
 * containing all other elements.
 *
 * Example: Partitioning a list into odd and even numbers
 *
 * partition([1, 2, 3, 4], val => val % 2 !== 0) => [[1, 3], [2, 4]]
 *
 * @param collection An array.
 * @param projection A function to be called for each item in the collection.
 * @returns A two-item array, the first containing a list of truthy values, and
 *          the second containing a list of falsey values.
 */
export function partition(collection, projection) {
    var matching = [];
    var nonMatching = [];
    collection.forEach(function (item) {
        if (projection(item)) {
            matching.push(item);
        }
        else {
            nonMatching.push(item);
        }
    });
    return [matching, nonMatching];
}
export function toNumber(value) {
    // check type OR truthiness to avoid 0 issues
    return typeof value === 'number' || value ? Number(value) : undefined;
}
// https://stackoverflow.com/questions/18082/validate-decimal-numbers-in-javascript-isnumeric
export function isNumeric(n) {
    return !Number.isNaN(parseFloat(n)) && Number.isFinite(Number(n));
}
export function flatten(arr) {
    return arr.reduce(function (acc, inner) { return acc.concat(inner); }, []);
}
/** Performs a deep comparison between two values to determine if they are equivalent.
 *
 * Signature same as lodash
 * @param value The value to compare.
 * @param other The other value to compare.
 * @returns Returns `true` if the values are equivalent, else `false`.
 */
export function isEqual(a, b) {
    if (a === b) {
        return true;
    }
    if (Array.isArray(a)) {
        return (Array.isArray(b) &&
            a.length === b.length &&
            a.every(function (value, i) { return isEqual(value, b[i]); }));
    }
    if (a instanceof Date) {
        return b instanceof Date && a.getTime() === b.getTime();
    }
    if (a instanceof RegExp) {
        return b instanceof RegExp && a.toString() === b.toString();
    }
    if (a instanceof Object && b instanceof Object) {
        var keysA = Object.keys(a);
        var keysB_1 = Object.keys(b);
        return (keysA.length === keysB_1.length &&
            keysA.every(function (key) { return keysB_1.includes(key) && isEqual(a[key], b[key]); }));
    }
    return false;
}
/**
 * Returns an object composed from key-value `pairs`.
 *
 * Signature same as lodash, see also https://github.com/tc39/proposal-object-from-entries
 * @param {Array} pairs The key-value pairs.
 * @returns {Object} Returns the new object.
 * @example
 *
 * _.fromPairs([['a', 1], ['b', 2]]);
 * // => { 'a': 1, 'b': 2 }
 */
export function fromPairs(values) {
    return values.reduce(function (acc, _a) {
        var _b;
        var key = _a[0], value = _a[1];
        return (__assign({}, acc, (_b = {}, _b[key] = value, _b)));
    }, {});
}
/**
 * Creates an object with the same keys as `object` and values generated
 * by running each own enumerable string keyed property of `object` thru
 * `iteratee`. The iteratee is invoked with three arguments:
 * (value, key, object).
 *
 * @param {Object} object The object to iterate over.
 * @param {Function} [iteratee=_.identity] The function invoked per iteration.
 * @returns {Object} Returns the new mapped object.
 * @example
 *
 * const users = {
 *   'fred':    { 'user': 'fred',    'age': 40 },
 *   'pebbles': { 'user': 'pebbles', 'age': 1 }
 * };
 *
 * _.mapValues(users, o => o.age);
 * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
 */
export function mapValues(object, iteratee) {
    return fromPairs(Object.entries(object).map(function (_a) {
        var key = _a[0], value = _a[1];
        return [key, iteratee(value, key)];
    }));
}
/**
 * Creates an object composed of keys generated from the results of running
 * each element of `collection` thru `iteratee`. The corresponding value of
 * each key is the last element responsible for generating the key. The
 * iteratee is invoked with one argument: (value).
 *
 * @param collection The collection to iterate over.
 * @param iteratee The iteratee to transform keys.
 * @returns Returns the composed aggregate object.
 * @example
 *
 * const array = [
 *   { 'dir': 'left', 'code': 97 },
 *   { 'dir': 'right', 'code': 100 }
 * ]
 *
 * keyBy(array, ({ code }) => String.fromCharCode(code))
 * // => { 'a': { 'dir': 'left', 'code': 97 }, 'd': { 'dir': 'right', 'code': 100 } }
 */
export function keyBy(collection, iteratee) {
    return fromPairs(collection.map(function (item) { return [iteratee(item), item]; }));
}
/** A version of JSON.stringify that sorts object keys
 * (and optionally array entries) for a stable sort.
 * Useful for using as an object's hash.
 *
 * Based on https://github.com/substack/json-stable-stringify
 */
export function jsonStableStringify(node, sortArrays) {
    if (sortArrays === void 0) { sortArrays = false; }
    if (node && node.toJSON && typeof node.toJSON === 'function') {
        node = node.toJSON();
    }
    if (node === undefined) {
        return;
    }
    if (typeof node !== 'object' || node === null) {
        return JSON.stringify(node);
    }
    if (Array.isArray(node)) {
        return "[" + (sortArrays ? node.slice().sort() : node)
            .map(function (value) { return jsonStableStringify(value, sortArrays) || JSON.stringify(null); })
            .join(',') + "]";
    }
    return "{" + Object.keys(node)
        .sort()
        .map(function (key) {
        var value = jsonStableStringify(node[key], sortArrays);
        return value ? JSON.stringify(key) + ":" + value : undefined;
    })
        .filter(isDefined)
        .join(',') + "}";
}
